import { Button, Card, InputNumber } from "antd";
import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";

import { Notify } from "../../../services/custom/Messages";
import { openCaptureNotification } from "../../custom/CaptureNotifications"

import { LocationAccessControl } from "@seims/shared";
import lookupService from "../../../services/lookupService";
import { GroupAccordionSection } from "./GroupAccordionSection";
import { useOfflineMutationQueue } from "../../../hooks/useOfflineMutationQueue";
import { recordError } from "../../../utils/logService";

import {
  PARTICIPANT_TYPES_STORE_NAME,
  PARTICIPANT_DISABILITIES_STORE_NAME,
  GENDERS_STORE_NAME,
  RACES_STORE_NAME,
  AGE_GROUPS_STORE_NAME,
  PROVINCES_DISTRICTS_TOWNS_STORE_NAME,
} from "../../../constants";
import { OFFLINE_GROUP_CAPTURE_QUEUE_KEY } from "../../../hooks/offlineQueueConfig";

const EMPTY_TALLIES = {
  type: {},
  district: {},
  disability: {},
  gender: {},
  race: {},
  ageGroup: {},
  repeat: { yes: 0, no: 0 },
};

export const GroupCapturing = () => {
  const { eventId } = useParams();
  const { coords, setOnlineCount } = useOutletContext();

  const [groupName, setGroupName] = useState("");
  /* -----------------------------
      EXPANSION STATE (FIX)
  ------------------------------*/
  const [expandedSection, setExpandedSection] = useState("type");
  const [expandedProvince, setExpandedProvince] = useState(null);

  /* -----------------------------
      LOOKUPS
  ------------------------------*/
  const [participantTypes, setParticipantTypes] = useState([]);
  const [disabilityTypes, setDisabilityTypes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [races, setRaces] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  /* -----------------------------
      TALLIES (CORE GROUP LOGIC)
  ------------------------------*/
  const [tallies, setTallies] = useState({
    type: {},
    district: {},
    disability: {},
    gender: {},
    race: {},
    ageGroup: {},
    repeat: { yes: 0, no: 0 },
  });

  /**
   OFFLINE CAPTURING 
   */
  const { runWithOfflineFallback } = useOfflineMutationQueue({
    storageKey: OFFLINE_GROUP_CAPTURE_QUEUE_KEY,

    onFlush: async ({ syncedCount, failedCount }) => {
      if (syncedCount > 0) {
        openCaptureNotification(
          "Group Capture",
          `${syncedCount} queued group capture${syncedCount > 1 ? "s" : ""} synced successfully`,
          Notify.SUCCESS
        );
      }

      if (failedCount > 0) {
        openCaptureNotification(
          "Group Capture",
          `${failedCount} queued group capture${failedCount > 1 ? "s" : ""} failed to sync`,
          Notify.ERROR
        );
      }
    },
  });
  /* -----------------------------
      LOAD LOOKUPS
  ------------------------------*/
  useEffect(() => {
    loadLookups();
  }, []);

  const loadLookups = async () => {
    try {
      const [
        participantResp,
        disabilityResp,
        genderResp,
        raceResp,
        ageGroupResp,
        provinceResp,
      ] = await Promise.all([
        lookupService.getCahedLookup(PARTICIPANT_TYPES_STORE_NAME),
        lookupService.getCahedLookup(PARTICIPANT_DISABILITIES_STORE_NAME),
        lookupService.getCahedLookup(GENDERS_STORE_NAME),
        lookupService.getCahedLookup(RACES_STORE_NAME),
        lookupService.getCahedLookup(AGE_GROUPS_STORE_NAME),
        lookupService.getCahedLookup(PROVINCES_DISTRICTS_TOWNS_STORE_NAME),
      ]);

      setParticipantTypes(participantResp?.data ?? []);
      setDisabilityTypes(disabilityResp?.data ?? []);
      setGenders(genderResp?.data ?? []);
      setRaces(raceResp?.data ?? []);
      setAgeGroups(ageGroupResp?.data ?? []);
      setProvinces(provinceResp?.data ?? []);
    } catch (err) {
      recordError(err,);
    }
  };


  /* -----------------------------
      HELPERS
  ------------------------------*/
  const REQUIRED_SECTIONS = [
    "type",
    "district",
    "gender",
    "race",
    "ageGroup",
    "disability",
    "repeat",
  ];

  const getSectionTotal = (section) =>
    Object.values(tallies[section] || {}).reduce(
      (sum, value) => sum + Number(value || 0), 0);

  const sectionTotals = {
    type: getSectionTotal("type"),
    district: getSectionTotal("district"),
    gender: getSectionTotal("gender"),
    race: getSectionTotal("race"),
    ageGroup: getSectionTotal("ageGroup"),
    disability: getSectionTotal("disability"),
    repeat: tallies.repeat.yes + tallies.repeat.no,
  };

  const totals = REQUIRED_SECTIONS.map(section => sectionTotals[section]);

  const targetTotal =
    totals.length > 0 ? Math.max(...totals) : 0;

  const isGroupValid =
    targetTotal > 0 &&
    REQUIRED_SECTIONS.every(
      section => sectionTotals[section] === targetTotal
    );

  const attendeeTotal = getSectionTotal("type");

  const buildProvinceTotals = () => {
    const provinceTotals = {};

    provinces.forEach((province) => {
      const totalForProvince = province.districts.reduce(
        (sum, district) =>
          sum + (tallies.district[String(district.key)] || 0),
        0
      );

      if (totalForProvince > 0) {
        provinceTotals[province.key] = totalForProvince;
      }
    });

    return provinceTotals;
  };

  const updateTally = (section, key, delta) => {
    const normalizedKey = String(key);

    setTallies(prev => {
      const current = prev[section][normalizedKey] || 0;
      const next = Math.max(0, current + delta);

      return {
        ...prev,
        [section]: {
          ...prev[section],
          [normalizedKey]: next,
        },
      };
    });
  };
  const setTallyValue = (section, key, value) => {
    const normalizedKey = String(key);

    setTallies(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [normalizedKey]:
          value === '' || value === null
            ? ''
            : Math.max(0, Number(value) || 0),
      },
    }));
  };
  const mapObjectToArray = (obj) =>
    Object.entries(obj).map(([key, value]) => ({
      key: Number(key),
      value,
    }));

  const repeatTotal = tallies.repeat.yes + tallies.repeat.no;

  const mapTalliesToArray = (section) =>
    Object.entries(tallies[section])
      .filter(([, value]) => value > 0)
      .map(([key, value]) => ({
        key: Number(key),
        value,
      }));

  const handleAddGroup = async () => {

    if (submitting) return;
    setSubmitting(true);

    //  Validation
    if (!isGroupValid) {
      openCaptureNotification(
        "Group Capturing",
        "All sections must have matching totals before submitting",
        Notify.WARNING
      );
      setSubmitting(false);
      return;
    }
    const provinceTotals = buildProvinceTotals();
    //  Build payload
    const payload = {
      groupName: groupName || null,

      type: mapTalliesToArray("type"),
      district: mapTalliesToArray("district"),
      province: mapObjectToArray(provinceTotals),
      gender: mapTalliesToArray("gender"),
      race: mapTalliesToArray("race"),
      ageGroup: mapTalliesToArray("ageGroup"),
      disability: mapTalliesToArray("disability"),

      repeat: {
        yes: tallies.repeat.yes,
        no: tallies.repeat.no,
      },

      recordDate: new Date().toISOString(),
      // Hard‑coded location for now
      location: {
        x: coords.latitude,
        y: coords.longitude,
      },
    };

    try {
      const { queued, response } = await runWithOfflineFallback({eventId, payload}, { count: targetTotal });

      if (queued) {
        openCaptureNotification(
          "Group Capture",
          "You are offline. Group capture was queued and will sync when online",
          Notify.INFO
        );

        // reset fields
        setTallies(EMPTY_TALLIES);
        setGroupName("");
        setExpandedSection("type");
        setExpandedProvince(null);
        return;
      }

      openCaptureNotification(
        "Group Capture",
        "Group participants added successfully",
        Notify.SUCCESS
      );

      // Every successful online capture returns total count
      if(response?.data?.total){
        setOnlineCount(response?.data?.total)
      }

      setTallies(EMPTY_TALLIES);
      setGroupName("");
      setExpandedSection("type");
      setExpandedProvince(null);

    } catch {
      openCaptureNotification(
        "Group Capture",
        "Failed to add group. Please try again",
        Notify.ERROR
      );

    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocationAccessControl>
      <Card style={{ borderRadius: 0, marginTop: 0 }} className="capture-attendees" data-testid="group-capture-form">
        <div className="group-name-field" style={{ marginBottom: 16, marginRight: 12}} data-testid="group-name-field">
          <label
            style={{
              display: "block",
              fontSize: 14,
              fontWeight: "bold",
              margin: "12px 12px 4px 12px",
              color: "#3F4345",
            }}
          >
            Group Name i.e. School name (optional)
          </label>

          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Enter group name"
            data-testid="group-name-input"
            style={{
              width: "100%",
              padding: "8px 12px",
              margin: "0px 12px",
              borderRadius: 4,
              border: "1px solid #707070",
            }}
          />
        </div>

        {/*  ATTENDEE TYPE */}
        <GroupAccordionSection
          title="Attendee Type"
          total={attendeeTotal}
          sectionId="group-type"
          expanded={expandedSection === "type"}
          onToggle={() =>
            setExpandedSection(
              expandedSection === "type" ? null : "type"
            )
          }
        >
          {participantTypes.map((item) => (
            <div key={item.key} className="tally-item" data-testid={`group-type-row-${item.key}`}>
              <span>{item.value}</span>
              <div className="tally-controls">
                <Button data-testid={`group-type-decrement-${item.key}`} onClick={() => updateTally("type", item.key, -1)}>
                  −
                </Button>
                <InputNumber
                  min={0}
                  value={
                    tallies.type[item.key] === ''
                      ? ''
                      : (tallies.type[item.key] ?? 0)
                  }
                  data-testid={`group-type-input-${item.key}`}
                  controls={false}
                  inputMode="numeric"
                  style={{ width: 56 }}
                  className="tally-input"
                  onFocus={() => {
                    if ((tallies.type[item.key] ?? 0) === 0) {
                      setTallyValue("type", item.key, '');
                    }
                  }}
                  onChange={(value) => {
                    setTallyValue("type", item.key, value);
                  }}
                  // Put 0 back if user leaves empty
                  onBlur={() => {
                    if (
                      tallies.type[item.key] === '' ||
                      tallies.type[item.key] === null ||
                      tallies.type[item.key] === undefined
                    ) {
                      setTallyValue("type", item.key, 0);
                    }
                  }}
                />
                <Button data-testid={`group-type-increment-${item.key}`} onClick={() => updateTally("type", item.key, 1)}>
                  +
                </Button>
              </div>
            </div>
          ))}
        </GroupAccordionSection>

        {/* DISABILITY TYPE */}
        <GroupAccordionSection
          title="Disability Type"
          total={getSectionTotal("disability")}
          sectionId="group-disability"
          expanded={expandedSection === "disability"}
          onToggle={() =>
            setExpandedSection(
              expandedSection === "disability" ? null : "disability"
            )
          }
        >
          {disabilityTypes.map((item) => (
            <div key={item.key} className="tally-item" data-testid={`group-disability-row-${item.key}`}>
              <span>{item.value}</span>

              <div className="tally-controls">
                <Button data-testid={`group-disability-decrement-${item.key}`} onClick={() => updateTally("disability", item.key, -1)}>
                  −
                </Button>
                <InputNumber
                  min={0}
                  value={
                    tallies.disability[item.key] === ''
                      ? ''
                      : (tallies.disability[item.key] ?? 0)
                  }
                  data-testid={`group-disability-input-${item.key}`}
                  onFocus={() => {
                    if ((tallies.disability[item.key] || 0) === 0) {
                      setTallyValue("disability", item.key, '');
                    }
                  }}
                  onChange={(value) => setTallyValue("disability", item.key, value)}

                  // ✅ Put 0 back if user leaves empty
                  onBlur={() => {
                    if (
                      tallies.disability[item.key] === '' ||
                      tallies.disability[item.key] === null ||
                      tallies.disability[item.key] === undefined
                    ) {
                      setTallyValue("disability", item.key, 0);
                    }
                  }}

                  controls={false}
                  inputMode="numeric"
                  style={{ width: 56 }}
                  className="tally-input"
                />
                <Button data-testid={`group-disability-increment-${item.key}`} onClick={() => updateTally("disability", item.key, 1)}>
                  +
                </Button>
              </div>
            </div>
          ))}
        </GroupAccordionSection>

        {/*  PROVINCE → DISTRICT (FIXED NESTED ACCORDION) */}
        <GroupAccordionSection
          title="Province & Districts"
          total={getSectionTotal("district")}
          sectionId="group-province"
          expanded={expandedSection === "province"}
          onToggle={() =>
            setExpandedSection(
              expandedSection === "province" ? null : "province"
            )
          }
        >
          {provinces.map((province) => {
            const provinceTotal = province.districts.reduce(
              (s, d) => s + (tallies.district[d.key] || 0),
              0
            );

            return (
              <GroupAccordionSection
                key={province.key}
                title={province.value}
                total={provinceTotal}
                sectionId={`group-province-${province.key}`}
                expanded={expandedProvince === province.key}
                onToggle={() =>
                  setExpandedProvince(
                    expandedProvince === province.key
                      ? null
                      : province.key
                  )
                }
              >
                {province.districts.map((district) => (
                  <div key={district.key} className="tally-item" data-testid={`group-district-row-${district.key}`}>
                    <span>{district.value}</span>
                    <div className="tally-controls">
                      <Button
                        data-testid={`group-district-decrement-${district.key}`}
                        onClick={() =>
                          updateTally("district", district.key, -1)
                        }
                      >
                        −
                      </Button>

                      <InputNumber
                        min={0}
                        value={
                          tallies.district[district.key] === ''
                            ? ''
                            : (tallies.district[district.key] ?? 0)
                      }
                        data-testid={`group-district-input-${district.key}`}
                        onFocus={() => {
                          if ((tallies.district[district.key] || 0) === 0) {
                            setTallyValue("district", district.key, null);
                          }
                        }}
                        onChange={(value) =>
                          setTallyValue("district", district.key, value)
                        }
                        //Put 0 back if user leaves empty
                        onBlur={() => {
                          if (
                            tallies.district[district.key] === '' ||
                            tallies.district[district.key] === null ||
                            tallies.district[district.key] === undefined
                          ) {
                            setTallyValue("district", district.key, 0);
                          }
                        }}
                        controls={false}
                        inputMode="numeric"
                        style={{ width: 56 }}
                        className="tally-input"
                      />

                      <Button
                        data-testid={`group-district-increment-${district.key}`}
                        // disabled={remainingFor("district") <= 0}
                        onClick={() =>
                          updateTally("district", district.key, 1)
                        }
                      >
                        +
                      </Button>
                    </div>
                  </div>
                ))}
              </GroupAccordionSection>
            );
          })}
        </GroupAccordionSection>

        {/*  GENDER */}
        <GroupAccordionSection
          title="Gender"
          total={getSectionTotal("gender")}
          sectionId="group-gender"
          expanded={expandedSection === "gender"}
          onToggle={() =>
            setExpandedSection(
              expandedSection === "gender" ? null : "gender"
            )
          }
        >
          {genders.map((item) => (
            <div key={item.key} className="tally-item" data-testid={`group-gender-row-${item.key}`}>
              <span>{item.value}</span>
              <div className="tally-controls">
                <Button data-testid={`group-gender-decrement-${item.key}`} onClick={() => updateTally("gender", item.key, -1)}>
                  −
                </Button>
                <InputNumber
                  min={0}
                  value={
                    tallies.gender[item.key] === ''
                      ? ''
                      : (tallies.gender[item.key] ?? 0)
                  }
                  data-testid={`group-gender-input-${item.key}`}
                  onFocus={() => {
                    if ((tallies.gender[item.key] || 0) === 0) {
                      setTallyValue("gender", item.key, null);
                    }
                  }}
                  onChange={(value) => setTallyValue("gender", item.key, value)}
                  //Put 0 back if user leaves empty
                  onBlur={() => {
                    if (
                      tallies.gender[item.key] === '' ||
                      tallies.gender[item.key] === null ||
                      tallies.gender[item.key] === undefined
                    ) {
                      setTallyValue("gender", item.key, 0);
                    }
                  }}
                  controls={false}
                  inputMode="numeric"
                  style={{ width: 56 }}
                  className="tally-input"
                />
                <Button
                  data-testid={`group-gender-increment-${item.key}`}
                  // disabled={remainingFor("gender") <= 0}
                  onClick={() => updateTally("gender", item.key, 1)}
                >
                  +
                </Button>
              </div>
            </div>
          ))}
        </GroupAccordionSection>

        {/*  RACE */}
        <GroupAccordionSection
          title="Race"
          total={getSectionTotal("race")}
          sectionId="group-race"
          expanded={expandedSection === "race"}
          onToggle={() =>
            setExpandedSection(
              expandedSection === "race" ? null : "race"
            )
          }
        >
          {races.map((item) => (
            <div key={item.key} className="tally-item" data-testid={`group-race-row-${item.key}`}>
              <span>{item.value}</span>
              <div className="tally-controls">
                <Button data-testid={`group-race-decrement-${item.key}`} onClick={() => updateTally("race", item.key, -1)}>
                  −
                </Button>
                <InputNumber
                  min={0}
                  value={
                    tallies.race[item.key] === ''
                      ? ''
                      : (tallies.race[item.key] ?? 0)
                  }
                  data-testid={`group-race-input-${item.key}`}
                  onFocus={() => {
                    if ((tallies.race[item.key] || 0) === 0) {
                      setTallyValue("race", item.key, null);
                    }
                  }}
                  onChange={(value) => setTallyValue("race", item.key, value)}
                  //Put 0 back if user leaves empty
                  onBlur={() => {
                    if (
                      tallies.race[item.key] === '' ||
                      tallies.race[item.key] === null ||
                      tallies.race[item.key] === undefined
                    ) {
                      setTallyValue("race", item.key, 0);
                    }
                  }}
                  controls={false}
                  inputMode="numeric"
                  style={{ width: 56 }}
                  className="tally-input"
                />
                <Button
                  data-testid={`group-race-increment-${item.key}`}
                  // disabled={remainingFor("race") <= 0}
                  onClick={() => updateTally("race", item.key, 1)}
                >
                  +
                </Button>
              </div>
            </div>
          ))}
        </GroupAccordionSection>

        {/*  AGE GROUP */}
        <GroupAccordionSection
          title="Age Group"
          total={getSectionTotal("ageGroup")}
          sectionId="group-age-group"
          expanded={expandedSection === "ageGroup"}
          onToggle={() =>
            setExpandedSection(
              expandedSection === "ageGroup" ? null : "ageGroup"
            )
          }
        >
          {ageGroups.map((item) => (
            <div key={item.key} className="tally-item" data-testid={`group-age-group-row-${item.key}`}>
              <span>{item.value}</span>
              <div className="tally-controls">
                <Button data-testid={`group-age-group-decrement-${item.key}`} onClick={() => updateTally("ageGroup", item.key, -1)}>
                  −
                </Button>
                <InputNumber
                  min={0}
                  value={
                    tallies.ageGroup[item.key] === ''
                      ? ''
                      : (tallies.ageGroup[item.key] ?? 0)
                  }
                  data-testid={`group-age-group-input-${item.key}`}
                  onFocus={() => {
                    if ((tallies.ageGroup[item.key] || 0) === 0) {
                      setTallyValue("ageGroup", item.key, null);
                    }
                  }}
                  onChange={(value) => setTallyValue("ageGroup", item.key, value)}
                  //Put 0 back if user leaves empty
                  onBlur={() => {
                    if (
                      tallies.ageGroup[item.key] === '' ||
                      tallies.ageGroup[item.key] === null ||
                      tallies.ageGroup[item.key] === undefined
                    ) {
                      setTallyValue("ageGroup", item.key, 0);
                    }
                  }}
                  controls={false}
                  inputMode="numeric"
                  style={{ width: 56 }}
                  className="tally-input"
                />
                <Button
                  data-testid={`group-age-group-increment-${item.key}`}
                  // disabled={remainingFor("ageGroup") <= 0}
                  onClick={() => updateTally("ageGroup", item.key, 1)}
                >
                  +
                </Button>
              </div>
            </div>
          ))}
        </GroupAccordionSection>

        {/* REPEAT Attendee */}
        <GroupAccordionSection
          title="Repeat Attendee"
          total={repeatTotal}
          sectionId="group-repeat"
          expanded={expandedSection === "repeat"}
          onToggle={() =>
            setExpandedSection(
              expandedSection === "repeat" ? null : "repeat"
            )
          }
        >
          {["yes", "no"].map((key) => (
            <div key={key} className="tally-item" data-testid={`group-repeat-row-${key}`}>
              <span>{key === "yes" ? "Yes" : "No"}</span>
              <div className="tally-controls">
                <Button
                  data-testid={`group-repeat-decrement-${key}`}
                  onClick={() =>
                    setTallies((prev) => ({
                      ...prev,
                      repeat: {
                        ...prev.repeat,
                        [key]: Math.max(0, prev.repeat[key] - 1),
                      },
                    }))
                  }
                >
                  −
                </Button>
                <InputNumber
                  min={0}
                  value={
                    tallies.repeat[key] === ''
                      ? ''
                      : (tallies.repeat[key] ?? 0)
                  }
                  data-testid={`group-repeat-input-${key}`}
                  onFocus={() => {
                    if ((tallies.repeat[key] || 0) === 0) {
                      setTallyValue("repeat", key, null);
                    }
                  }}
                  onChange={(value) => {
                    const safeValue = Math.max(0, Number(value) || 0);
                    setTallies(prev => ({
                      ...prev,
                      repeat: {
                        ...prev.repeat,
                        [key]: safeValue,
                      },
                    }));
                  }}
                  //Put 0 back if user leaves empty
                  onBlur={() => {
                    if (
                      tallies.repeat[key] === '' ||
                      tallies.repeat[key] === null ||
                      tallies.repeat[key] === undefined
                    ) {
                      setTallyValue("repeat", key, 0);
                    }
                  }}
                  controls={false}
                  inputMode="numeric"
                  style={{ width: 56 }}
                  className="tally-input"
                />
                <Button
                  data-testid={`group-repeat-increment-${key}`}
                  // disabled={remainingRepeat <= 0}
                  onClick={() =>
                    setTallies((prev) => ({
                      ...prev,
                      repeat: {
                        ...prev.repeat,
                        [key]: prev.repeat[key] + 1,
                      },
                    }))
                  }
                >
                  +
                </Button>
              </div>
            </div>
          ))}
        </GroupAccordionSection>

        <br />
        <div className="footer-holder">
          <Button
            className="group-footer"
            type="primary"
            block
            data-testid="add-group-submit"
            style={{
              background: "#1F7AB6",
              borderRadius: 20,
              padding: "6px 28px",
              fontWeight: 500,
              border: "none",
            }}
            onClick={handleAddGroup}
            loading={submitting}
            disabled={submitting}
          >
            + Add Group
          </Button>
        </div>
        <br /><br />
      </Card>
    </LocationAccessControl>
  );
};
